import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Escape a value for safe interpolation into innerHTML in BOTH text and
 * (single- or double-quoted) attribute contexts. Escapes `& < > " '` so a
 * crafted merchant string cannot break out of an attribute or inject markup.
 *
 * @param {unknown} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Format a byte count as a short human-readable file size (e.g. "512 KB", "2.4 MB").
 *
 * @param {number} bytes
 * @returns {string}
 */
function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.0', '')} MB`;
}

/**
 * Error whose message is safe to show to the shopper (a curated label or a
 * message Shopify itself returned, e.g. "sold out"). Internal/technical throws
 * stay as plain `Error` so they never leak jargon into the storefront.
 */
class UserFacingError extends Error {}

/** @returns {string} A collision-resistant id that works in non-secure contexts too. */
function generateGroupId() {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return `ace-${uuid}`;
  return `ace-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @typedef {Object} PersonalizationMethod
 * @property {string} key
 * @property {string} label
 * @property {unknown} allowedCodesRaw
 * @property {null | {
 *   id: number,
 *   handle: string,
 *   title: string,
 *   images: Array<{id: number, src: string, variantIds: number[]}>,
 *   variants: Array<{id: number, title: string, priceCents: number, imageId: number | null, size: string | null}>
 * }} serviceProduct
 */

/**
 * @typedef {Object} PositionEntry
 * @property {string} code
 * @property {string} label
 * @property {number} variantId
 * @property {number} serviceProductId
 * @property {number} priceCents
 * @property {string | null} size
 * @property {string | null} imageUrl
 */

/**
 * @typedef {Object} PersonalizationParentLine
 * @property {number} variantId
 * @property {number} quantity
 */

/**
 * @typedef {Object} BulkSummaryLine
 * @property {number} variantId
 * @property {number} quantity
 * @property {number} priceCents
 */

/**
 * @typedef {Object} StickdateiConfig
 * @property {number} serviceProductId
 * @property {string} title
 * @property {unknown} largeCodesRaw
 * @property {Array<{id: number, title: string, priceCents: number}>} variants
 */

/**
 * @typedef {Object} PersonalizationConfig
 * @property {string} productFormId
 * @property {number} parentProductId
 * @property {string} parentProductTitle
 * @property {string} currencyCode
 * @property {string} moneyFormat
 * @property {number} vatRate
 * @property {string} [allowedFileFormats]
 * @property {{[key: string]: string}} labels
 * @property {number} [moq]
 * @property {StickdateiConfig | null} [stickdatei]
 * @property {Array<{id: number, priceCents: number, title: string}>} parentVariants
 * @property {PersonalizationMethod[]} methods
 */

/**
 * @typedef {Object} PersonalizationRefs
 * @property {HTMLElement} methods
 * @property {HTMLElement} positions
 * @property {HTMLElement} positionsGroup
 * @property {HTMLElement} selectedCount
 * @property {HTMLElement} garmentSummaryPrice
 * @property {HTMLElement} personalizationSummaryPrice
 * @property {HTMLElement} bundleSummaryPrice
 * @property {HTMLElement} error
 */

/**
 * @extends {Component<PersonalizationRefs>}
 */
class AceProductPersonalization extends Component {
  requiredRefs = [
    'methods',
    'positions',
    'positionsGroup',
    'selectedCount',
    'error',
  ];

  /** @type {PersonalizationConfig | null} */
  #config = null;

  /** @type {Map<string, PersonalizationMethod & {positions: PositionEntry[]}>} */
  #methods = new Map();

  /** @type {Map<string, File>} */
  #filesByCode = new Map();

  /** @type {Map<string, string>} */
  #previewUrlsByCode = new Map();

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {string | null} */
  #activeMethodKey = null;

  /** @type {Set<string>} */
  #selectedCodes = new Set();

  /** @type {{ active: boolean, lines: BulkSummaryLine[], garmentTotal: number, quantity: number, invalid: boolean } | null} */
  #bulkSummary = null;

  /** @type {boolean} */
  #expanded = false;

  /** @type {boolean} Guards against overlapping cart writes on double-submit. */
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();

    this.#config = this.#parseConfig();
    if (!this.#config) return;

    this.#methods = this.#normalizeMethods(this.#config.methods);
    this.#form = this.#findForm();

    this.#ensureActiveMethod();
    this.#initDisclosure();
    this.#renderMethods();
    this.#renderPositions();
    this.#updateSummary();
    this.#syncProductFormState();

    // Guard the click on `window` (capture) so it runs before component.js's
    // document-level delegation — otherwise add-to-cart's handleClick animates
    // an "added" confirmation before we can block an invalid selection.
    window.addEventListener('click', this.#onFormClick, true);
    this.#form?.addEventListener('submit', this.#onFormSubmit, true);
    this.#form?.addEventListener('input', this.#onFormInput);
    this.#form?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener('ace:bulk-variant-order:update', this.#onBulkVariantOrderUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('click', this.#onFormClick, true);
    this.#form?.removeEventListener('submit', this.#onFormSubmit, true);
    this.#form?.removeEventListener('input', this.#onFormInput);
    this.#form?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener('ace:bulk-variant-order:update', this.#onBulkVariantOrderUpdate);
    for (const code of [...this.#previewUrlsByCode.keys()]) {
      this.#clearPreviewUrl(code);
    }
  }

  /** @returns {boolean} */
  isActive() {
    return Boolean(this.#activeMethodKey) && this.#selectedCodes.size > 0;
  }

  /** @returns {boolean} */
  validateSelection() {
    const moq = this.#moq();
    const total = this.#totalQuantity();
    if (total < moq) {
      const template = this.#config?.labels.moqError || 'Personalisierung ist ab [min] Stück möglich (aktuell [count]).';
      this.#setError(template.replace('[min]', String(moq)).replace('[count]', String(total)));
      return false;
    }

    const selected = this.#getSelectedPersonalization();
    if (!selected) return false;

    this.#setError('');
    return true;
  }

  /** @returns {number} Minimum total garment quantity before personalization is offered. */
  #moq() {
    return Math.max(1, Number(this.#config?.moq) || 1);
  }

  /** @returns {number} Total garment quantity: bulk rows summed across variants, else the quantity field. */
  #totalQuantity() {
    if (this.#bulkSummary?.active) return Math.max(0, Number(this.#bulkSummary.quantity) || 0);
    return this.#getQuantity();
  }

  /**
   * @param {PersonalizationParentLine[]} lines
   * @param {string} [sourceId]
   * @returns {Promise<{ sections?: Record<string, string>, itemCount: number }>}
   */
  async addPersonalizedVariantLines(lines, sourceId) {
    void sourceId;

    if (!this.validateSelection()) {
      throw new Error('Personalisierungsauswahl ist ungültig');
    }

    const selected = this.#getSelectedPersonalization();
    if (!selected) {
      throw new Error('Personalisierungsauswahl fehlt');
    }

    const normalizedLines = lines
      .map((line) => ({
        variantId: Number(line.variantId) || 0,
        quantity: Math.max(1, Number(line.quantity) || 0),
      }))
      .filter((line) => line.variantId > 0 && line.quantity > 0);

    if (normalizedLines.length === 0) {
      throw new Error('Keine personalisierten Varianten zum Hinzufügen');
    }

    const sections = this.#collectSections();
    const parentGroupIds = [];
    let latestSections;
    let feeParentLineKey = null;

    try {
      for (const line of normalizedLines) {
        const groupId = this.#createGroupId();
        const parentResponse = await this.#addParentLine({
          groupId,
          method: selected.method,
          selectedPositions: selected.selectedPositions,
          selectedLabels: selected.selectedLabels,
          quantity: line.quantity,
          parentVariantId: line.variantId,
          parentTitle: this.#config?.parentProductTitle || '',
          sections,
        });
        parentGroupIds.push(groupId);

        const parentCartItem = await this.#resolveParentCartItem(parentResponse, groupId);
        const parentLineKey = parentCartItem?.key || '';
        if (!parentLineKey) {
          throw new Error('Warenkorb-Schlüssel des Hauptprodukts fehlt');
        }
        if (!this.#hasUploadedFileUrls(parentCartItem, selected.selectedPositions)) {
          throw new UserFacingError(
            this.#config?.labels.uploadFailedError || 'Logo-Datei konnte von Shopify nicht hochgeladen werden.'
          );
        }

        const childResponse = await this.#addChildLines({
          groupId,
          method: selected.method,
          selectedPositions: selected.selectedPositions,
          selectedLabels: selected.selectedLabels,
          quantity: line.quantity,
          parentVariantId: line.variantId,
          parentTitle: this.#config?.parentProductTitle || '',
          sections,
          parentLineKey,
          parentCartItem,
        });

        latestSections = childResponse.sections || parentResponse.sections || latestSections;

        // One-time digitizing fees are charged once per order action, anchored
        // to the first parent line (they roll back with it on failure).
        if (feeParentLineKey === null) {
          feeParentLineKey = parentLineKey;
          const feeResponse = await this.#addFeeLines({
            groupId,
            method: selected.method,
            parentLineKey,
            parentVariantId: line.variantId,
            sections,
          });
          latestSections = feeResponse?.sections || latestSections;
        }
      }

      return {
        sections: latestSections,
        itemCount: normalizedLines.reduce((sum, line) => sum + line.quantity, 0),
      };
    } catch (error) {
      for (const groupId of parentGroupIds.reverse()) {
        try {
          await this.#rollbackParent(groupId);
        } catch (rollbackError) {
          console.error('Failed to rollback personalized parent line', rollbackError);
        }
      }

      const genericFailure =
        this.#config?.labels.serviceFailedError || 'Personalisierung konnte nicht hinzugefügt werden.';
      const failureMessage =
        error instanceof UserFacingError && error.message ? error.message : genericFailure;

      if (error instanceof Error) {
        error.cause = error.cause || new Error(failureMessage);
      }

      this.#setError(failureMessage);
      throw error;
    }
  }

  /** @returns {PersonalizationConfig | null} */
  #parseConfig() {
    const script = this.querySelector('[data-ace-personalization-config]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;

    try {
      return /** @type {PersonalizationConfig} */ (JSON.parse(script.textContent));
    } catch (error) {
      console.error('Failed to parse ACE personalization config', error);
      return null;
    }
  }

  /**
   * @param {PersonalizationMethod[]} methods
   * @returns {Map<string, PersonalizationMethod & {positions: PositionEntry[]}>}
   */
  #normalizeMethods(methods) {
    const normalized = new Map();

    for (const method of methods) {
      /** @type {PositionEntry[]} */
      const positions = [];
      const allowedCodeSet = new Set(this.#normalizeAllowedCodes(method.allowedCodesRaw));

      if (method.serviceProduct) {
        for (const variant of method.serviceProduct.variants) {
          const match = String(variant.title || '').match(/^([A-Z])\s*-\s*(.+)$/);
          if (!match) continue;

          const code = match[1]?.toUpperCase() || '';
          const label = match[2]?.trim() || variant.title;
          if (!code) continue;
          if (!allowedCodeSet.has(code)) continue;

          const imageUrl = this.#resolveImageUrl(method.serviceProduct.images, variant.imageId, variant.id);
          positions.push({
            code,
            label,
            variantId: Number(variant.id),
            serviceProductId: Number(method.serviceProduct.id),
            priceCents: Number(variant.priceCents) || 0,
            size: variant.size || null,
            imageUrl,
          });
        }
      }

      positions.sort((left, right) => left.code.localeCompare(right.code));
      normalized.set(method.key, { ...method, positions });
    }

    return normalized;
  }

  /**
   * @param {unknown} rawValue
   * @returns {string[]}
   */
  #normalizeAllowedCodes(rawValue) {
    if (Array.isArray(rawValue)) {
      return rawValue
        .map((value) => String(value ?? '').trim().toUpperCase())
        .filter(Boolean);
    }

    if (typeof rawValue === 'string') {
      return rawValue
        .replace(/;/g, ',')
        .split(',')
        .map((value) => value.trim().toUpperCase())
        .filter(Boolean);
    }

    if (rawValue == null) {
      return [];
    }

    return String(rawValue)
      .replace(/;/g, ',')
      .split(',')
      .map((value) => value.trim().toUpperCase())
      .filter(Boolean);
  }

  /**
   * @param {Array<{id: number, src: string, variantIds: number[]}>} images
   * @param {number | null} imageId
   * @param {number} variantId
   * @returns {string | null}
   */
  #resolveImageUrl(images, imageId, variantId) {
    if (imageId) {
      const exact = images.find((image) => Number(image.id) === Number(imageId));
      if (exact?.src) return exact.src;
    }

    const fallback = images.find((image) => Array.isArray(image.variantIds) && image.variantIds.includes(Number(variantId)));
    return fallback?.src || null;
  }

  /** @returns {HTMLFormElement | null} */
  #findForm() {
    const formId = this.dataset.productFormId || this.#config?.productFormId;
    if (!formId) return null;
    const form = document.getElementById(formId);
    return form instanceof HTMLFormElement ? form : null;
  }

  #ensureActiveMethod() {
    const activeMethod = this.#activeMethodKey ? this.#methods.get(this.#activeMethodKey) : null;
    if (activeMethod && this.#methodIsAvailable(activeMethod)) return;

    const firstAvailable = [...this.#methods.values()].find(
      (method) => this.#methodIsAvailable(method)
    );
    this.#activeMethodKey = firstAvailable?.key || null;
  }

  /**
   * @param {PersonalizationMethod & {positions: PositionEntry[]}} method
   * @returns {boolean}
   */
  #methodIsAvailable(method) {
    return Boolean(method.serviceProduct) && method.positions.length > 0;
  }

  #renderMethods() {
    this.refs.methods.replaceChildren();

    for (const method of this.#methods.values()) {
      if (!this.#methodIsAvailable(method)) continue;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ace-product-personalization__method';
      button.textContent = method.label;
      button.dataset.methodKey = method.key;
      button.setAttribute('aria-pressed', method.key === this.#activeMethodKey ? 'true' : 'false');

      button.addEventListener('click', () => {
        this.#setActiveMethod(method.key);
      });

      this.refs.methods.appendChild(button);
    }
  }

  #initDisclosure() {
    const toggleButton = this.querySelector('[data-personalization-toggle]');
    if (toggleButton instanceof HTMLButtonElement) {
      toggleButton.addEventListener('click', () => {
        this.#setExpanded(!this.#expanded);
      });
    }

    const clearButton = this.querySelector('[data-personalization-clear]');
    if (clearButton instanceof HTMLButtonElement) {
      clearButton.addEventListener('click', () => {
        this.#clearSelection();
      });
    }

    this.#setExpanded(false);
  }

  /**
   * @param {boolean} expanded
   */
  #setExpanded(expanded) {
    this.#expanded = expanded;
    const panel = this.querySelector('[data-personalization-panel]');
    const toggleButton = this.querySelector('[data-personalization-toggle]');

    if (panel instanceof HTMLElement) {
      panel.classList.toggle('hidden', !expanded);
    }

    if (toggleButton instanceof HTMLButtonElement) {
      toggleButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    this.dataset.expanded = expanded ? 'true' : 'false';
  }

  #clearSelection() {
    this.#selectedCodes.clear();
    this.#filesByCode.clear();
    for (const code of [...this.#previewUrlsByCode.keys()]) {
      this.#clearPreviewUrl(code);
    }

    this.#setExpanded(false);
    this.#renderPositions();
    this.#updateSummary();
    this.#syncProductFormState();
    this.#setError('');
  }

  /**
   * @param {string} key
   */
  #setActiveMethod(key) {
    if (this.#activeMethodKey === key) return;

    const activeMethod = this.#methods.get(key);
    if (!activeMethod || !this.#methodIsAvailable(activeMethod)) return;

    this.#activeMethodKey = key;
    const allowed = new Set(activeMethod?.positions.map((position) => position.code) || []);
    for (const code of [...this.#selectedCodes]) {
      if (!allowed.has(code)) {
        this.#selectedCodes.delete(code);
        this.#filesByCode.delete(code);
        this.#clearPreviewUrl(code);
      }
    }

    this.#renderMethods();
    this.#renderPositions();
    this.#updateSummary();
    this.#syncProductFormState();
    this.#setError('');
  }

  #renderPositions() {
    this.refs.positions.replaceChildren();

    const method = this.#activeMethodKey ? this.#methods.get(this.#activeMethodKey) : null;
    const positions = method?.positions || [];

    this.refs.positionsGroup.classList.toggle('hidden', positions.length === 0);
    if (positions.length === 0) {
      this.refs.selectedCount.textContent = '';
      return;
    }

    this.refs.selectedCount.textContent = (this.#config?.labels.selectedCount || '').replace(
      '[count]',
      String(this.#selectedCodes.size)
    );

    const acceptedFileFormats = this.#acceptedFileFormats();
    const groups = this.#groupPositions(positions);

    for (const group of groups) {
      const section = document.createElement('section');
      section.className = 'ace-product-personalization__position-section';
      section.innerHTML = `
        <div class="ace-product-personalization__section-heading">
          <h4 class="ace-product-personalization__section-title">${escapeHtml(group.label)}</h4>
        </div>
        <div class="ace-product-personalization__position-grid"></div>
      `;

      const grid = section.querySelector('.ace-product-personalization__position-grid');
      if (!(grid instanceof HTMLElement)) continue;

      for (const position of group.positions) {
        const card = this.#renderPositionCard(position);
        grid.appendChild(card);
      }

      // A logo is always uploaded for a concrete placement, so the upload field
      // only appears once a position in this group is selected.
      if (group.positions.some((position) => this.#selectedCodes.has(position.code))) {
        section.appendChild(this.#renderGroupUpload(group, acceptedFileFormats));
      }

      this.refs.positions.appendChild(section);
    }
  }

  /**
   * @param {PositionEntry} position
   * @returns {HTMLElement}
   */
  #renderPositionCard(position) {
      const checked = this.#selectedCodes.has(position.code);
      const card = document.createElement('label');
      card.className = `ace-product-personalization__position${
        checked ? ' ace-product-personalization__position--selected' : ''
      }`;
      card.setAttribute('aria-label', `${position.code} - ${position.label}`);

      card.innerHTML = `
        <input class="ace-product-personalization__position-input" type="checkbox" data-code="${escapeHtml(position.code)}" ${
          checked ? 'checked' : ''
        }>
        <div class="ace-product-personalization__position-inner">
          <div class="ace-product-personalization__position-face ace-product-personalization__position-face--front">
            <div class="ace-product-personalization__position-media${
              position.imageUrl ? '' : ' ace-product-personalization__position-media--placeholder'
            }">
              ${
                position.imageUrl
                  ? `<img src="${escapeHtml(position.imageUrl)}" alt="${escapeHtml(position.label)}">`
                  : `<span>${escapeHtml(position.code)}</span>`
              }
            </div>
          </div>
          <div class="ace-product-personalization__position-face ace-product-personalization__position-face--back">
            <span class="ace-product-personalization__position-code">${escapeHtml(position.code)}</span>
            <span class="ace-product-personalization__position-label">${escapeHtml(position.label)}</span>
            ${
              position.size
                ? `<span class="ace-product-personalization__position-size">${escapeHtml(position.size)}</span>`
                : ''
            }
            <span class="ace-product-personalization__position-price">${formatMoney(position.priceCents, this.#config?.moneyFormat || '${{amount}}', this.#config?.currencyCode || 'EUR')} ${escapeHtml(this.#config?.labels.perPiece || 'pro Stück')}</span>
          </div>
        </div>
      `;

      const checkbox = card.querySelector(`[data-code="${position.code}"]`);
      if (checkbox instanceof HTMLInputElement) {
        checkbox.addEventListener('change', () => {
          if (checkbox.checked) {
            this.#selectedCodes.add(position.code);
            this.#inheritGroupFile(position);
          } else {
            this.#selectedCodes.delete(position.code);
            this.#filesByCode.delete(position.code);
            this.#clearPreviewUrl(position.code);
          }

          this.#renderPositions();
          this.#updateSummary();
          this.#syncProductFormState();
          this.#setError('');
        });
      }

      return card;
  }

  /**
   * @param {{key: string, label: string, positions: PositionEntry[]}} group
   * @param {string} acceptedFileFormats
   * @returns {HTMLElement}
   */
  #renderGroupUpload(group, acceptedFileFormats) {
    const selectedPositions = group.positions.filter((position) => this.#selectedCodes.has(position.code));
    const firstSelectedWithFile = selectedPositions.find((position) => this.#filesByCode.has(position.code));
    const previewCode = firstSelectedWithFile?.code || '';
    const previewUrl = previewCode ? this.#previewUrlsByCode.get(previewCode) || '' : '';
    const file = previewCode ? this.#filesByCode.get(previewCode) : null;
    const isPreviewableImage = file ? file.type.startsWith('image/') : false;
    const acceptedFileFormatLabel = this.#acceptedFileFormatLabel();
    const fileName = file ? escapeHtml(file.name) : '';
    const fileSize = file ? formatFileSize(file.size) : '';
    const upload = document.createElement('div');
    upload.className = 'ace-product-personalization__upload';
    upload.innerHTML = `
      ${
        file
          ? `
            <div class="ace-product-personalization__upload-preview">
              <button type="button" class="ace-product-personalization__upload-delete" data-upload-delete-group="${escapeHtml(group.key)}" aria-label="${escapeHtml(this.dataset.removeLogoLabel || 'Logo entfernen')}">
                <span aria-hidden="true">×</span>
              </button>
              ${
                previewUrl && isPreviewableImage
                  ? `<img src="${escapeHtml(previewUrl)}" alt="${escapeHtml((this.#config?.labels.previewAlt || 'Logo Vorschau für {{ position }}').replace('{{ position }}', group.label))}">`
                  : `
                    <div class="ace-product-personalization__upload-file">
                      <span class="ace-product-personalization__upload-file-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" focusable="false">
                          <path d="M6 2.75h8.1L19 7.65v13.6H6V2.75Zm2 2v14.5h9V8.8h-4.05V4.75H8Zm6.45.7v1.9h1.9l-1.9-1.9Z" />
                        </svg>
                      </span>
                      <span class="ace-product-personalization__upload-file-body">
                        <span class="ace-product-personalization__upload-file-title">${fileName}</span>
                        <span class="ace-product-personalization__upload-file-meta">${escapeHtml(fileSize || this.#config?.labels.fileUploaded || 'Datei hochgeladen')}</span>
                      </span>
                    </div>
                  `
              }
            </div>
          `
          : `
            <label class="ace-product-personalization__upload-field">
              <input type="file" accept="${escapeHtml(acceptedFileFormats)}" data-upload-input-group="${escapeHtml(group.key)}">
              <span class="ace-product-personalization__upload-content">
                <span class="ace-product-personalization__upload-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false">
                    <path d="M7 18a5 5 0 0 1 .6-9.96A6 6 0 0 1 19 10.5a3.75 3.75 0 0 1-.75 7.42H16v-2.5h2.1A1.25 1.25 0 0 0 18 12.93l-1.27-.04-.18-1.26a3.5 3.5 0 0 0-6.85-.46l-.3 1.13-1.16-.18A2.5 2.5 0 0 0 7 15.5h2v2.5H7Zm3.75 0v-4.1H8.9L12 10.8l3.1 3.1h-1.85V18h-2.5Z" />
                  </svg>
                </span>
                <span class="ace-product-personalization__upload-title">${escapeHtml(this.#config?.labels.uploadTitle || 'Logo hochladen')}</span>
                <span class="ace-product-personalization__upload-formats">${escapeHtml(this.#config?.labels.acceptedFormats || 'Akzeptierte Formate')}: ${escapeHtml(acceptedFileFormatLabel)}</span>
              </span>
            </label>
          `
      }
    `;

    const fileInput = upload.querySelector(`[data-upload-input-group="${group.key}"]`);
    if (fileInput instanceof HTMLInputElement) {
      fileInput.addEventListener('change', () => {
        const nextFile = fileInput.files?.[0];
        if (selectedPositions.length === 0) {
          fileInput.value = '';
          this.#setError(this.#config?.labels.missingSelectionError || 'Wähle mindestens eine Position.');
          return;
        }

        if (nextFile) {
          if (!this.#fileMatchesAcceptedFormats(nextFile)) {
            fileInput.value = '';
            for (const position of selectedPositions) {
              this.#filesByCode.delete(position.code);
              this.#clearPreviewUrl(position.code);
            }
            this.#renderPositions();
            this.#syncProductFormState();
            this.#setError(this.#fileInvalidError());
            return;
          }

          for (const position of selectedPositions) {
            this.#filesByCode.set(position.code, nextFile);
            this.#setPreviewUrl(position.code, nextFile);
          }
        }

        this.#renderPositions();
        this.#syncProductFormState();
        this.#setError('');
      });
    }

    const deleteButton = upload.querySelector(`[data-upload-delete-group="${group.key}"]`);
    if (deleteButton instanceof HTMLButtonElement) {
      deleteButton.addEventListener('click', () => {
        for (const position of selectedPositions) {
          this.#filesByCode.delete(position.code);
          this.#clearPreviewUrl(position.code);
        }
        this.#renderPositions();
        this.#syncProductFormState();
        this.#setError('');
      });
    }

    return upload;
  }

  /**
   * @param {PositionEntry[]} positions
   * @returns {Array<{key: string, label: string, positions: PositionEntry[]}>}
   */
  #groupPositions(positions) {
    const front = { key: 'front', label: this.#config?.labels.groupFront || 'Logo Vorne', positions: [] };
    const back = { key: 'back', label: this.#config?.labels.groupBack || 'Logo Hinten', positions: [] };
    const ordered = [front, back];
    const otherGroups = new Map();

    for (const position of positions) {
      const target = this.#positionGroup(position);
      if (target === 'front') {
        front.positions.push(position);
      } else if (target === 'back') {
        back.positions.push(position);
      } else {
        // Every non front/back placement is its own group so unrelated positions
        // (e.g. a sleeve and a cap) never share one uploaded logo.
        let group = otherGroups.get(target);
        if (!group) {
          group = {
            key: target,
            label: position.label || this.#config?.labels.groupOther || 'Weitere Logo Position',
            positions: [],
          };
          otherGroups.set(target, group);
          ordered.push(group);
        }
        group.positions.push(position);
      }
    }

    return ordered.filter((group) => group.positions.length > 0);
  }

  /** @returns {PositionEntry[]} */
  #activePositions() {
    const method = this.#activeMethodKey ? this.#methods.get(this.#activeMethodKey) : null;
    return method?.positions || [];
  }

  /**
   * A single logo is shared across all positions in a group. When a position is
   * newly selected, adopt the file already uploaded for a sibling in the same
   * group so it isn't left fileless (which would block add-to-cart while the UI
   * shows the group as complete).
   *
   * @param {PositionEntry} position
   */
  #inheritGroupFile(position) {
    if (this.#filesByCode.has(position.code)) return;
    const group = this.#positionGroup(position);
    const donor = this.#activePositions().find(
      (candidate) =>
        candidate.code !== position.code &&
        this.#selectedCodes.has(candidate.code) &&
        this.#filesByCode.has(candidate.code) &&
        this.#positionGroup(candidate) === group
    );
    if (!donor) return;
    const file = this.#filesByCode.get(donor.code);
    if (!file) return;
    this.#filesByCode.set(position.code, file);
    this.#setPreviewUrl(position.code, file);
  }

  /**
   * Front and back positions are grouped so their siblings can share one logo.
   * Every other placement is keyed by its own code so unrelated positions never
   * share an uploaded file.
   *
   * @param {PositionEntry} position
   * @returns {string} 'front', 'back', or `other:<code>`
   */
  #positionGroup(position) {
    const label = `${position.code} ${position.label}`.toLowerCase();
    if (label.includes('hinten') || label.includes('rücken') || label.includes('ruecken') || label.includes('back')) {
      return 'back';
    }
    if (label.includes('vorne') || label.includes('brust') || label.includes('front')) {
      return 'front';
    }
    return `other:${position.code}`;
  }

  #updateSummary() {
    // The summary box only carries information once a personalization is
    // active; without one it would just repeat the product price.
    const scope = this.closest('.ace-product-personalization-shell') || this;
    const summary = scope.querySelector('[ref="summary"]');
    if (summary instanceof HTMLElement) {
      summary.classList.toggle('hidden', !this.isActive());
    }

    const method = this.#activeMethodKey ? this.#methods.get(this.#activeMethodKey) : null;
    const selectedPositions = (method?.positions || []).filter((position) => this.#selectedCodes.has(position.code));

    const quantity = this.#bulkSummary ? this.#bulkSummary.quantity : this.#getQuantity();
    const garmentTotal = this.#bulkSummary
      ? this.#bulkSummary.lines.reduce(
          (sum, line) => sum + this.#cartRoundedLineTotal(line.priceCents, line.quantity),
          0
        )
      : this.#cartRoundedLineTotal(this.#getCurrentParentPrice(), quantity);
    const childTotal = selectedPositions.reduce((sum, position) => {
      if (this.#bulkSummary) {
        return (
          sum +
          this.#bulkSummary.lines.reduce(
            (lineSum, line) => lineSum + this.#cartRoundedLineTotal(position.priceCents, line.quantity),
            0
          )
        );
      }

      return sum + this.#cartRoundedLineTotal(position.priceCents, quantity);
    }, 0);

    // One-time embroidery digitizing fees: per logo, independent of quantity.
    const fees = this.#stickdateiFees();
    const feeTotal = fees.reduce((sum, fee) => sum + this.#cartRoundedLineTotal(fee.priceCents, 1), 0);
    const feeRow = scope.querySelector('[data-stickdatei-row]');
    if (feeRow instanceof HTMLElement) {
      feeRow.classList.toggle('hidden', feeTotal === 0);
    }
    this.#setRefText('stickdateiSummaryPrice', this.#money(feeTotal));

    const bundleTotal = garmentTotal + childTotal + feeTotal;

    this.#setRefText('garmentSummaryPrice', this.#money(garmentTotal));
    this.#setRefText('personalizationSummaryPrice', this.#money(childTotal));
    this.#setRefText('bundleSummaryPrice', this.#money(bundleTotal));
    this.refs.selectedCount.textContent = (this.#config?.labels.selectedCount || '').replace(
      '[count]',
      String(this.#selectedCodes.size)
    );

    this.#updateMoqGate();
  }

  /**
   * Personalization is only offered from a minimum total garment quantity
   * (summed across bulk variant rows). Below it the toggle is disabled with an
   * upsell hint; an already-active selection stays visible but add-to-cart is
   * blocked via validateSelection().
   */
  #updateMoqGate() {
    const moq = this.#moq();
    const total = this.#totalQuantity();
    const locked = total < moq;

    const toggle = this.querySelector('[data-personalization-toggle]');
    if (toggle instanceof HTMLButtonElement) {
      toggle.disabled = locked && !this.isActive();
    }

    const hint = this.querySelector('[data-personalization-moq-hint]');
    if (hint instanceof HTMLElement) {
      if (locked && moq > 1) {
        const template = this.#config?.labels.moqHint || 'Logo-Druck ab [min] Stück — füge noch [remaining] Stück hinzu.';
        hint.textContent = template
          .replace('[min]', String(moq))
          .replace('[remaining]', String(Math.max(1, moq - total)));
        hint.classList.remove('hidden');
      } else {
        hint.classList.add('hidden');
      }
    }

    if (locked && !this.isActive() && this.#expanded) {
      this.#setExpanded(false);
    }
  }

  /**
   * One-time embroidery digitizing fees ("Stickdatei-Erstellung"): one fee per
   * logo. A logo is shared by all positions of a position group (front/back/
   * other), so the fee is charged per selected group — billed at the large rate
   * when any selected position of the group is a large placement (default
   * A, D, E), otherwise at the small rate. Non-embroidery methods have none.
   *
   * @returns {Array<{ groupKey: string, groupLabel: string, variantId: number, priceCents: number }>}
   */
  #stickdateiFees() {
    const stickdatei = this.#config?.stickdatei;
    const method = this.#activeMethodKey ? this.#methods.get(this.#activeMethodKey) : null;
    if (!stickdatei || !method || method.key !== 'embroidery') return [];

    const variants = Array.isArray(stickdatei.variants) ? stickdatei.variants : [];
    if (variants.length === 0) return [];

    const sorted = [...variants].sort((a, b) => (Number(a.priceCents) || 0) - (Number(b.priceCents) || 0));
    const small = sorted.find((variant) => /klein|small/i.test(String(variant.title))) || sorted[0];
    const large = sorted.find((variant) => /gro(ß|ss)|large/i.test(String(variant.title))) || sorted[sorted.length - 1];
    const largeCodes = new Set(this.#normalizeAllowedCodes(stickdatei.largeCodesRaw));

    const selectedPositions = method.positions.filter((position) => this.#selectedCodes.has(position.code));
    const groups = this.#groupPositions(selectedPositions);

    return groups
      .map((group) => {
        const isLarge = group.positions.some((position) => largeCodes.has(position.code));
        const variant = isLarge ? large : small;
        return {
          groupKey: group.key,
          groupLabel: group.label,
          variantId: Number(variant?.id) || 0,
          priceCents: Number(variant?.priceCents) || 0,
        };
      })
      .filter((fee) => fee.variantId > 0);
  }

  /**
   * @param {string} ref
   * @param {string} value
   */
  #setRefText(ref, value) {
    // The summary refs (garment/personalization/bundle prices) live in a sibling
    // node just outside this custom element, so `this.querySelector` cannot reach
    // them. Scope to this instance's block shell instead — it wraps exactly one
    // component and its summary, so sibling component instances on the same page
    // (e.g. multiple products) still never receive each other's prices.
    const scope = this.closest('.ace-product-personalization-shell') || this;
    const target = scope.querySelector(`[ref="${ref}"]`);
    if (target instanceof HTMLElement) {
      target.textContent = value;
    }
  }

  /** @returns {number} */
  #getCurrentParentPrice() {
    const variantId = this.#getCurrentParentVariantId();
    const variant = this.#config?.parentVariants.find((entry) => Number(entry.id) === variantId);
    if (variant?.priceCents != null) {
      return Number(variant.priceCents) || 0;
    }

    const fallbackFromPriceComponent = this.#readPriceFromDom();
    if (fallbackFromPriceComponent != null) {
      return fallbackFromPriceComponent;
    }

    const firstVariant = this.#config?.parentVariants?.[0];
    return Number(firstVariant?.priceCents) || 0;
  }

  /** @returns {number} */
  #getCurrentParentVariantId() {
    if (!this.#form) return 0;
    const input = this.#form.querySelector('input[name="id"]');
    return input instanceof HTMLInputElement ? Number(input.value) || 0 : 0;
  }

  /** @returns {number | null} */
  #readPriceFromDom() {
    const priceElement = document.querySelector('product-price [ref="priceContainer"] .price');
    const priceText = priceElement?.textContent?.trim();
    if (!priceText) return null;

    const normalized = priceText
      .replace(/[^\d,.-]/g, '')
      .replace(/\.(?=\d{3}(?:\D|$))/g, '')
      .replace(',', '.');

    const parsed = Number.parseFloat(normalized);
    if (!Number.isFinite(parsed)) return null;

    return Math.round(parsed * 100);
  }

  /** @returns {number} */
  #getQuantity() {
    if (!this.#form) return 1;
    const input = this.#form.querySelector('input[name="quantity"]');
    return input instanceof HTMLInputElement ? Math.max(1, Number(input.value) || 1) : 1;
  }

  /**
   * Reproduces the cart's line-level VAT rounding from an already-grossed unit
   * price. For configured VAT factors (> 1), rounding the gross unit price is
   * injective at cent precision, so dividing by the factor recovers the original
   * integer net unit price before the complete line is grossed and rounded.
   *
   * @param {number} grossUnitCents
   * @param {number} quantity
   * @returns {number}
   */
  #cartRoundedLineTotal(grossUnitCents, quantity) {
    const unitPrice = Math.max(0, Math.round(Number(grossUnitCents) || 0));
    const lineQuantity = Math.max(0, Math.round(Number(quantity) || 0));
    const vatRate = Number(this.#config?.vatRate) || 1;

    if (vatRate <= 1) return unitPrice * lineQuantity;

    const netUnitPrice = Math.round(unitPrice / vatRate);
    return Math.round(netUnitPrice * lineQuantity * vatRate);
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    return formatMoney(cents, this.#config?.moneyFormat || '${{amount}}', this.#config?.currencyCode || 'EUR');
  }

  #syncProductFormState() {
    if (!this.#form) return;

    const isActive = Boolean(this.#activeMethodKey) && this.#selectedCodes.size > 0;
    this.#form.dataset.aceLogoEnabled = isActive ? 'true' : 'false';
    this.#form.dataset.acePersonalizationEnabled = isActive ? 'true' : 'false';
    this.dataset.active = isActive ? 'true' : 'false';
  }

  /**
   * @param {string} message
   */
  #setError(message) {
    const hasError = message.length > 0;
    this.refs.error.textContent = message;
    this.refs.error.classList.toggle('hidden', !hasError);
    // Make the error impossible to miss: bring it into view when it appears
    // (e.g. uploading a logo before selecting a position).
    if (hasError) {
      this.refs.error.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /** @param {MouseEvent} event */
  #onFormClick = (event) => {
    if (!this.isActive()) return;
    if (!(event.target instanceof Element)) return;

    const addToCart = event.target.closest('add-to-cart-component');
    if (!addToCart || addToCart.closest('form') !== this.#form) return;

    if (this.validateSelection()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #onFormInput = () => {
    this.#updateSummary();
  };

  #onQuantitySelectorUpdate = () => {
    this.#updateSummary();
  };

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const detail = /** @type {{ detail?: { data?: { productId?: string } } }} */ (event);
    if (String(detail.detail?.data?.productId || '') !== String(this.#config?.parentProductId || '')) return;
    this.#bulkSummary = null;
    this.#updateSummary();
  };

  /** @param {Event} event */
  #onBulkVariantOrderUpdate = (event) => {
    const detail = /** @type {{ detail?: { productId?: string, active?: boolean, lines?: BulkSummaryLine[], garmentTotal?: number, quantity?: number, invalid?: boolean } }} */ (event);
    if (String(detail.detail?.productId || '') !== String(this.#config?.parentProductId || '')) return;

    const lines = Array.isArray(detail.detail?.lines) ? detail.detail.lines : [];
    if (detail.detail?.active === false) {
      this.#bulkSummary = null;
    } else {
      this.#bulkSummary = {
        active: true,
        lines,
        garmentTotal: Number(detail.detail?.garmentTotal) || 0,
        quantity: Number(detail.detail?.quantity) || 0,
        invalid: Boolean(detail.detail?.invalid),
      };
    }

    this.#updateSummary();
  };

  /** @param {SubmitEvent} event */
  #onFormSubmit = async (event) => {
    if (!this.isActive()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!this.validateSelection()) return;

    const bulkLines = this.#bulkSummary?.active ? this.#bulkSummary.lines : [];
    const lines =
      this.#bulkSummary?.active
        ? bulkLines.map((line) => ({
            variantId: Number(line.variantId) || 0,
            quantity: Math.max(1, Number(line.quantity) || 0),
          }))
        : [
            {
              variantId: this.#getCurrentParentVariantId(),
              quantity: this.#getQuantity(),
            },
          ];
    const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
    if (this.#bulkSummary?.active && (this.#bulkSummary.invalid || itemCount < 1)) {
      this.#setError(this.#config?.labels.bulkRowRequired || 'Bitte wähle mindestens eine vollständige Bulk-Varianten-Zeile aus.');
      return;
    }
    if (lines.some((line) => !line.variantId) || itemCount < 1 || !this.#form) return;
    if (this.#submitting) return;

    this.#setError('');
    this.#submitting = true;

    try {
      const data = await this.addPersonalizedVariantLines(lines);

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'ace-product-personalization',
          itemCount,
          productId: String(this.#config?.parentProductId || ''),
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      const message =
        error instanceof UserFacingError && error.message
          ? error.message
          : this.#config?.labels.serviceFailedError || 'Personalisierung konnte nicht hinzugefügt werden.';
      this.dispatchEvent(new CartErrorEvent(this.id, message, {}, {}));
      this.#setError(message);
    } finally {
      this.#submitting = false;
    }
  };

  /**
   * @returns {{ method: PersonalizationMethod & {positions: PositionEntry[]}, selectedPositions: PositionEntry[], selectedLabels: string } | null}
   */
  #getSelectedPersonalization() {
    const method = this.#activeMethodKey ? this.#methods.get(this.#activeMethodKey) : null;
    if (!method) return null;

    const selectedPositions = method.positions.filter((position) => this.#selectedCodes.has(position.code));
    if (selectedPositions.length === 0) {
      this.#setError(this.#config?.labels.missingSelectionError || 'Wähle mindestens eine Position.');
      return null;
    }

    for (const position of selectedPositions) {
      const file = this.#filesByCode.get(position.code);
      if (!file) {
        this.#setError(this.#config?.labels.fileRequiredError || 'Lade für jede Position eine Datei hoch.');
        return null;
      }

      if (!this.#fileMatchesAcceptedFormats(file)) {
        this.#setError(this.#fileInvalidError());
        return null;
      }
    }

    return {
      method,
      selectedPositions,
      selectedLabels: selectedPositions.map((position) => position.label).join(', '),
    };
  }

  /** @returns {string} */
  #createGroupId() {
    return generateGroupId();
  }

  /** @returns {string[]} */
  #collectSections() {
    const sections = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sections.push(item.dataset.sectionId);
      }
    }

    return sections;
  }

  /**
   * @param {{
   *  groupId: string,
   *  method: PersonalizationMethod & {positions: PositionEntry[]},
   *  selectedPositions: PositionEntry[],
   *  selectedLabels: string,
   *  quantity: number,
   *  parentVariantId: number,
   *  parentTitle: string,
   *  sections: string[]
   * }} payload
   */
  async #addParentLine(payload) {
    if (!this.#form) throw new Error('Produktformular nicht gefunden');

    const formData = new FormData(this.#form);
    formData.set('id', String(payload.parentVariantId));
    formData.set('quantity', String(payload.quantity));

    for (const sectionId of payload.sections) {
      formData.append('sections', sectionId);
    }
    formData.append('sections_url', window.location.pathname);

    formData.append('properties[_ace_bundle]', 'true');
    formData.append('properties[_ace_bundle_role]', 'parent');
    formData.append('properties[_ace_bundle_group_id]', payload.groupId);
    formData.append('properties[_ace_bundle_method]', payload.method.key);
    formData.append('properties[_ace_parent_variant_id]', String(payload.parentVariantId));
    formData.append('properties[_ace_selected_codes]', payload.selectedPositions.map((position) => position.code).join(','));

    for (const position of payload.selectedPositions) {
      const file = this.#filesByCode.get(position.code);
      if (!file) continue;
      formData.append(`properties[${this.#uploadPropertyKey(position)}]`, file);
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new UserFacingError(`Hauptprodukt konnte nicht hinzugefügt werden (HTTP ${response.status})`, { cause: parseError });
      }
      if (!response.ok || data.status) {
        throw new UserFacingError(data?.message || `Hauptprodukt konnte nicht hinzugefügt werden (HTTP ${response.status})`);
      }
      return data;
    } catch (error) {
      if (error instanceof UserFacingError) throw error;
      throw new Error('Hauptprodukt konnte nicht hinzugefügt werden', { cause: error });
    }
  }

  /**
   * @param {{
   *  groupId: string,
   *  method: PersonalizationMethod & {positions: PositionEntry[]},
   *  selectedPositions: PositionEntry[],
   *  selectedLabels: string,
   *  quantity: number,
   *  parentVariantId: number,
   *  parentTitle: string,
   *  sections: string[]
   *  parentLineKey: string
   *  parentCartItem: { key?: string, properties?: Record<string, string> } | null
   * }} payload
   */
  async #addChildLines(payload) {
    const propertyLabels = {
      placement: this.#config?.labels.positionLabel || 'Position',
      size: this.#config?.labels.sizeLabel || 'Größe',
      method: this.#config?.labels.methodLabel || 'Druckart',
      logoFile: this.#config?.labels.logoFileLabel || 'Logo-Datei',
    };
    const items = payload.selectedPositions.map((position) => ({
      id: position.variantId,
      quantity: payload.quantity,
      parent_line_key: payload.parentLineKey,
      properties: {
        [propertyLabels.placement]: position.label,
        [propertyLabels.size]: position.size || '',
        [propertyLabels.method]: payload.method.label,
        [propertyLabels.logoFile]: this.#uploadedFileUrl(payload.parentCartItem, position),
        '_ace_bundle': 'true',
        '_ace_bundle_role': 'child',
        '_ace_bundle_group_id': payload.groupId,
        '_ace_bundle_method': payload.method.key,
        '_ace_parent_variant_id': String(payload.parentVariantId),
        '_ace_service_product_id': String(position.serviceProductId),
        '_ace_service_variant_id': String(position.variantId),
        '_ace_placement_code': position.code,
        '_ace_placement_label': position.label,
        '_ace_size': position.size || '',
        '_ace_upload_property_key': this.#uploadPropertyKey(position),
      },
    }));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: payload.sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        await this.#rollbackParent(payload.groupId);
        throw new UserFacingError(`Personalisierungsposition konnte nicht hinzugefügt werden (HTTP ${response.status})`, { cause: parseError });
      }
      if (!response.ok || data.status) {
        await this.#rollbackParent(payload.groupId);
        throw new UserFacingError(data?.message || `Personalisierungsposition konnte nicht hinzugefügt werden (HTTP ${response.status})`);
      }

      return data;
    } catch (error) {
      if (error instanceof UserFacingError) throw error;
      throw new Error('Personalisierungsposition konnte nicht hinzugefügt werden', { cause: error });
    }
  }

  /**
   * @param {any} response
   * @param {string} groupId
   * @returns {Promise<{ key?: string, properties?: Record<string, string> } | null>}
   */
  async #resolveParentCartItem(response, groupId) {
    const addedItems = Array.isArray(response?.items) ? response.items : [];
    const parentFromResponse =
      addedItems.find((item) => {
        const properties = item?.properties || {};
        return properties._ace_bundle_group_id === groupId && properties._ace_bundle_role === 'parent';
      }) || null;

    if (parentFromResponse?.key) {
      return parentFromResponse;
    }

    return this.#findParentCartItem(groupId, { retries: 4, delayMs: 150 });
  }

  /**
   * @param {PositionEntry} position
   * @returns {string}
   */
  #uploadPropertyKey(position) {
    return `_${position.code} logo file`;
  }

  /**
   * @param {string} groupId
   */
  /**
   * Adds the one-time embroidery digitizing-fee lines ("Stickdatei-Erstellung"):
   * one line per logo, always quantity 1, marked `_ace_fee` so the cart's
   * parent→child quantity sync leaves them alone. Anchored to the given parent
   * line, so removing that parent (or a rollback) removes the fees too.
   *
   * @param {{
   *  groupId: string,
   *  method: PersonalizationMethod & {positions: PositionEntry[]},
   *  parentLineKey: string,
   *  parentVariantId: number,
   *  sections: string[]
   * }} payload
   * @returns {Promise<{ sections?: Record<string, string> } | null>}
   */
  async #addFeeLines(payload) {
    const fees = this.#stickdateiFees();
    if (fees.length === 0) return null;

    const propertyLabels = {
      placement: this.#config?.labels.positionLabel || 'Position',
      method: this.#config?.labels.methodLabel || 'Druckart',
    };

    const items = fees.map((fee) => ({
      id: fee.variantId,
      quantity: 1,
      parent_line_key: payload.parentLineKey,
      properties: {
        [propertyLabels.placement]: fee.groupLabel,
        [propertyLabels.method]: payload.method.label,
        '_ace_bundle': 'true',
        '_ace_bundle_role': 'child',
        '_ace_fee': 'stickdatei',
        '_ace_bundle_group_id': payload.groupId,
        '_ace_bundle_method': payload.method.key,
        '_ace_parent_variant_id': String(payload.parentVariantId),
        '_ace_service_variant_id': String(fee.variantId),
      },
    }));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: payload.sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new UserFacingError(`Stickdatei-Gebühr konnte nicht hinzugefügt werden (HTTP ${response.status})`, {
          cause: parseError,
        });
      }
      if (!response.ok || data.status) {
        throw new UserFacingError(data?.message || `Stickdatei-Gebühr konnte nicht hinzugefügt werden (HTTP ${response.status})`);
      }
      return data;
    } catch (error) {
      if (error instanceof UserFacingError) throw error;
      throw new Error('Stickdatei-Gebühr konnte nicht hinzugefügt werden', { cause: error });
    }
  }

  async #rollbackParent(groupId) {
    const parentItem = await this.#findParentCartItem(groupId);

    if (!parentItem?.key) return;

    const sections = this.#collectSections();
    await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: parentItem.key,
        quantity: 0,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
  }

  /**
   * @param {string} groupId
   * @returns {Promise<{ key?: string, properties?: Record<string, string> } | null>}
   */
  async #findParentCartItem(groupId, options = {}) {
    const retries = Math.max(0, Number(options.retries) || 0);
    const delayMs = Math.max(0, Number(options.delayMs) || 0);

    for (let attempt = 0; attempt <= retries; attempt += 1) {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: {
          Accept: 'application/json',
        },
      });
      let cart;
      try {
        cart = await cartResponse.json();
      } catch {
        cart = { items: [] };
      }
      const parentItem =
        (cart.items || []).find((item) => {
          const properties = item.properties || {};
          return properties._ace_bundle_group_id === groupId && properties._ace_bundle_role === 'parent';
        }) || null;

      if (parentItem?.key) {
        return parentItem;
      }

      if (attempt < retries && delayMs > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, delayMs));
      }
    }

    return null;
  }

  /**
   * @param {{ properties?: Record<string, string> } | null} parentCartItem
   * @param {PositionEntry[]} positions
   * @returns {boolean}
   */
  #hasUploadedFileUrls(parentCartItem, positions) {
    return positions.every((position) => this.#uploadedFileUrl(parentCartItem, position).length > 0);
  }

  /**
   * @param {{ properties?: Record<string, string> } | null} parentCartItem
   * @param {PositionEntry} position
   * @returns {string}
   */
  #uploadedFileUrl(parentCartItem, position) {
    const properties = parentCartItem?.properties || {};
    const uploadKey = this.#uploadPropertyKey(position);
    const value = properties[uploadKey];
    return typeof value === 'string' ? value : '';
  }

  /**
   * @param {string} code
   * @param {File} file
   */
  #setPreviewUrl(code, file) {
    this.#clearPreviewUrl(code);
    if (!file.type.startsWith('image/')) return;
    this.#previewUrlsByCode.set(code, URL.createObjectURL(file));
  }

  /**
   * @param {string} code
   */
  #clearPreviewUrl(code) {
    const currentUrl = this.#previewUrlsByCode.get(code);
    if (currentUrl) {
      URL.revokeObjectURL(currentUrl);
      this.#previewUrlsByCode.delete(code);
    }
  }

  /**
   * @returns {string}
   */
  #acceptedFileFormats() {
    return this.#acceptedFileFormatTokens().join(',');
  }

  /**
   * @returns {string}
   */
  #acceptedFileFormatLabel() {
    return this.#acceptedFileFormatTokens()
      .map((format) => {
        if (format === '*/*') return this.#config?.labels.allFormats || 'alle Formate';
        if (format === 'image/*') return this.#config?.labels.imageFormats || 'Bilder';
        if (format.startsWith('.')) return format.slice(1);
        return format;
      })
      .join(', ');
  }

  /**
   * @returns {string[]}
   */
  #acceptedFileFormatTokens() {
    const rawFormats = this.#config?.allowedFileFormats || 'pdf, ai, eps, svg, png, jpg, jpeg, webp';
    const tokens = rawFormats
      .split(',')
      .map((format) => format.trim().toLowerCase())
      .filter(Boolean)
      .map((format) => {
        if (format.includes('/')) return format;
        return format.startsWith('.') ? format : `.${format}`;
      })
      .filter((format) => {
        if (format === '*/*') return true;
        if (format.startsWith('.')) return /^\.[a-z0-9+-]+$/i.test(format);
        return /^[a-z0-9.+-]+\/[a-z0-9.+*-]+$/i.test(format);
      });

    // Only the formats configured in the editor are accepted — images (image/*,
    // .png, …) are allowed exclusively when the merchant lists them, so a
    // vectors-only config no longer silently accepts raster uploads.
    const editorTokens = tokens.length > 0 ? tokens : ['.pdf', '.ai', '.eps', '.svg'];

    return [...new Set(editorTokens)];
  }

  /**
   * @param {File} file
   * @returns {boolean}
   */
  #fileMatchesAcceptedFormats(file) {
    const tokens = this.#acceptedFileFormatTokens();
    if (tokens.includes('*/*')) return true;

    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();
    const dotIndex = fileName.lastIndexOf('.');
    const extension = dotIndex >= 0 ? fileName.slice(dotIndex) : '';

    return tokens.some((format) => {
      if (format.startsWith('.')) return extension === format;
      if (format.endsWith('/*')) return fileType.startsWith(format.slice(0, -1));
      return fileType === format;
    });
  }

  /**
   * @returns {string}
   */
  #fileInvalidError() {
    const message = this.#config?.labels.fileInvalidError || 'Bitte lade eine Datei in einem erlaubten Format hoch.';
    return `${message} (${this.#acceptedFileFormatLabel()})`;
  }
}

if (!customElements.get('ace-product-personalization')) {
  customElements.define('ace-product-personalization', AceProductPersonalization);
}
